import { Check, Copy, Info } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/shadcn/button';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { useDataPointDoc } from '@/contexts/AnalyticsDocsContext';
import { useTranslations } from '@/hooks/useTranslations';
import type { DataPointDoc } from '@/types/analytics';

interface DataPointInfoButtonProps {
    /** Looks the doc up from AnalyticsDocsContext — used by the pre-defined boards. */
    docKey?: string;
    /** Or pass one directly — used by custom-dashboard widgets, whose docs come with the result. */
    doc?: DataPointDoc;
    className?: string;
}

/**
 * The "(i)" on a KPI tile, chart card, or dashboard widget: opens a modal
 * explaining what the figure is made of, in plain language and in SQL.
 *
 * Renders nothing when the card has no documentation, so a partially documented
 * board simply shows fewer buttons rather than dead affordances.
 */
export default function DataPointInfoButton({ docKey, doc: explicitDoc, className }: DataPointInfoButtonProps) {
    const { t } = useTranslations();
    const contextDoc = useDataPointDoc(docKey);
    const doc = explicitDoc ?? contextDoc;

    const [open, setOpen] = useState(false);

    if (!doc) {
        return null;
    }

    const statements = typeof doc.sql === 'string' ? [{ label: '', sql: doc.sql }] : doc.sql;

    return (
        <>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className={className ?? 'text-muted-foreground/60 hover:text-foreground h-7 w-7'}
                aria-label={t('analytics.docs.open_info', 'How this figure is calculated')}
                onClick={() => setOpen(true)}
            >
                <Info className="h-4 w-4" />
            </Button>

            <ResponsiveModal open={open} onOpenChange={setOpen} variant="dialog">
                <ResponsiveModalContent dialogClassName="sm:max-w-2xl">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{doc.label}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>{doc.description}</ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="space-y-6">
                        {doc.points.length > 0 && (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold">{t('analytics.docs.data_points', 'Data points')}</h3>
                                <dl className="space-y-3">
                                    {doc.points.map((point) => (
                                        <div key={point.label} className="border-border border-l-2 pl-3">
                                            <dt className="text-sm font-medium">{point.label}</dt>
                                            <dd className="text-muted-foreground text-sm">{point.description}</dd>
                                            {point.source && (
                                                <dd className="text-muted-foreground/80 mt-0.5 font-mono text-xs">{point.source}</dd>
                                            )}
                                        </div>
                                    ))}
                                </dl>
                            </section>
                        )}

                        {doc.notes && doc.notes.length > 0 && (
                            <section className="space-y-2">
                                <h3 className="text-sm font-semibold">{t('analytics.docs.how_calculated', 'How it is calculated')}</h3>
                                <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                                    {doc.notes.map((note) => (
                                        <li key={note}>{note}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">{t('analytics.docs.sql', 'SQL')}</h3>
                            {statements.map((statement, index) => (
                                <SqlBlock key={statement.label || index} label={statement.label} sql={statement.sql} />
                            ))}
                        </section>
                    </ResponsiveModalBody>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </>
    );
}

function SqlBlock({ label, sql }: { label: string; sql: string }) {
    const { t } = useTranslations();
    const [copied, setCopied] = useState(false);

    const copy = () => {
        void navigator.clipboard.writeText(sql).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="space-y-1">
            {label && <p className="text-muted-foreground text-xs font-medium">{label}</p>}
            <div className="bg-muted relative rounded-md">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-7 w-7"
                    aria-label={copied ? t('analytics.docs.copied', 'Copied') : t('analytics.docs.copy', 'Copy SQL')}
                    onClick={copy}
                >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
                {/* Wide statements scroll inside this box so the modal itself never does. */}
                <div className="overflow-x-auto">
                    <pre className="p-3 pr-10 font-mono text-xs leading-relaxed whitespace-pre">{sql}</pre>
                </div>
            </div>
        </div>
    );
}
